import { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    ClickAwayListener,
    Grid,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography,
    useMediaQuery
} from '@mui/material';

// project imports
import { toogleAppIsLoading } from 'store/slices/main';
import { useDispatch, useSelector } from 'store';
import Transitions from 'ui-component/extended/Transitions';
import LocaleFlagIcon from 'ui-component/custom/LocaleFlagIcon';

// project hooks
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';

// ==============================|| LOCALIZATION ||============================== //

const LocalizationSection = () => {
    const dispatch = useDispatch();
    const { borderRadius, locale, setConfigByName } = useConfig();
    const { updateProfileSetting } = useAuth();

    const { appIsLoading } = useSelector((state) => state.main);

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    [theme.breakpoints.down('md')]: { ml: 1 }
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        transition: 'all .2s ease-in-out',
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.light
                        }
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={() => setOpen((prevOpen) => !prevOpen)}
                    color="inherit"
                >
                    <Typography variant="h5" sx={{ textTransform: 'uppercase' }} color="inherit">
                        <LocaleFlagIcon lang={locale} />
                    </Typography>
                </Avatar>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom-start' : 'bottom'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [matchesXs ? 0 : 0, 20] } }] }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={(event) => {
                            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                                return;
                            }

                            setOpen(false);
                        }}
                    >
                        <Transitions position={matchesXs ? 'top-left' : 'top'} in={open} {...TransitionProps}>
                            <Paper elevation={16}>
                                {open && (
                                    <List
                                        component="nav"
                                        sx={{
                                            width: '100%',
                                            minWidth: 200,
                                            maxWidth: 280,
                                            bgcolor: theme.palette.background.paper,
                                            borderRadius: `${borderRadius}px`,
                                            [theme.breakpoints.down('md')]: { maxWidth: 250 }
                                        }}
                                    >
                                        <ListItemButton
                                            selected={locale === 'pt'}
                                            disabled={appIsLoading}
                                            onClick={() => {
                                                dispatch(toogleAppIsLoading(true));
                                                updateProfileSetting('locale', { fieldDataType: 'string', fieldValue: 'pt' }, () =>
                                                    batch(() => {
                                                        setOpen(false);
                                                        setConfigByName('locale', 'pt');
                                                        dispatch(toogleAppIsLoading(false));
                                                        window.location.reload(true);
                                                    })
                                                );
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <LocaleFlagIcon lang="pt" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">Português</Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                        <ListItemButton
                                            selected={locale === 'en'}
                                            disabled={appIsLoading}
                                            onClick={() => {
                                                dispatch(toogleAppIsLoading(true));
                                                updateProfileSetting('locale', { fieldDataType: 'string', fieldValue: 'en' }, () =>
                                                    batch(() => {
                                                        setOpen(false);
                                                        setConfigByName('locale', 'en');
                                                        dispatch(toogleAppIsLoading(false));
                                                        window.location.reload(true);
                                                    })
                                                );
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <LocaleFlagIcon lang="en" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">English</Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                        <ListItemButton
                                            selected={locale === 'es'}
                                            disabled={appIsLoading}
                                            onClick={() => {
                                                dispatch(toogleAppIsLoading(true));
                                                updateProfileSetting('locale', { fieldDataType: 'string', fieldValue: 'es' }, () =>
                                                    batch(() => {
                                                        setOpen(false);
                                                        setConfigByName('locale', 'es');
                                                        dispatch(toogleAppIsLoading(false));
                                                        window.location.reload(true);
                                                    })
                                                );
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <LocaleFlagIcon lang="es" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Grid container>
                                                        <Typography color="textPrimary">Español</Typography>
                                                    </Grid>
                                                }
                                            />
                                        </ListItemButton>
                                    </List>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default LocalizationSection;
